import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, CircularProgress, Alert, Stack, TextField, 
  InputAdornment, IconButton, TablePagination, Tooltip, Grid, Card, CardContent,
  Avatar, Chip, ToggleButton, ToggleButtonGroup, FormControl, Select, MenuItem, InputLabel, Grow
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [countryFilter, setCountryFilter] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${config}/leads`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }   
      });
      setLeads(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setError('Failed to fetch leads from Dynamics 365');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (statecode) => {
    if (statecode === 1) return 'Qualified (Converted)';
    if (statecode === 2) return 'Disqualified';
    return 'Open';
  };

  const getStatusColor = (statecode) => {
    if (statecode === 1) return 'success';
    if (statecode === 2) return 'error';
    return 'primary';
  };

  const filteredLeads = leads.filter(lead => {
    let matchFilter = true;
    if (filter === 'Open') matchFilter = lead.statecode === 0;
    else if (filter === 'Qualified (Converted)') matchFilter = lead.statecode === 1;
    else if (filter === 'Disqualified') matchFilter = lead.statecode === 2;
    else if (filter === 'App Created') matchFilter = lead.description === 'Created via ProjectManager App';
    else if (filter === 'Last 7 Days') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      sevenDaysAgo.setHours(0, 0, 0, 0);
      const leadDate = new Date(lead.createdon);
      matchFilter = leadDate >= sevenDaysAgo;
    }

    if (!matchFilter) return false;
    
    if (countryFilter !== 'All' && lead.address1_country !== countryFilter) return false;

    return (
      (lead.fullname?.toLowerCase() || '').includes(search.toLowerCase()) ||      
      (lead.companyname?.toLowerCase() || '').includes(search.toLowerCase()) ||   
      (lead.emailaddress1?.toLowerCase() || '').includes(search.toLowerCase())    
    );
  }).sort((a, b) => new Date(b.createdon) - new Date(a.createdon));

  const uniqueCountries = ['All', ...new Set(leads.map(lead => lead.address1_country).filter(Boolean))].sort();

  const displayedLeads = filteredLeads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f4f6f8' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Header Section */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            mt: 2, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Background Decorative Circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3 }}>
                <Box>
                    <Typography variant="h3" fontWeight="800" sx={{ letterSpacing: -1, color: 'white', fontSize: { xs: '2rem', md: '2.5rem' }, mb: 1 }}>
                        Leads Directory
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Manage generated leads seamlessly
                    </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => navigate('/leads/new')}
                        sx={{ whiteSpace: 'nowrap', borderRadius: 3, bgcolor: 'white', color: '#1e3c72', fontWeight: 'bold', boxShadow: '0 4px 14px rgba(0,0,0,0.1)', '&:hover': { bgcolor: '#f0f0f0' }, px: 3, py: 1.5 }}
                    >
                        New Lead
                    </Button>
                </Box>
            </Box>
        </Box>
      </Grow>

      <Grow in={true} timeout={800}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Controls */}
      <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {['All', 'Open', 'Qualified (Converted)', 'Disqualified', 'App Created', 'Last 7 Days'].map(f => (
              <Chip
                key={f}
                label={f}
                onClick={() => setFilter(f)}
                sx={{ 
                  fontWeight: 'bold', 
                  borderRadius: 2, 
                  px: 1,
                  bgcolor: filter === f ? '#eff6ff' : '#f8fafc',
                  color: filter === f ? '#2563eb' : '#64748b',
                  border: '1px solid',
                  borderColor: filter === f ? '#bfdbfe' : '#e2e8f0',
                  '&:hover': { bgcolor: filter === f ? '#dbeafe' : '#f1f5f9' },
                  transition: 'all 0.2s ease'
                }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', md: 'auto' } }}>
          <TextField
            select
            size="small"
            value={countryFilter}
            onChange={(e) => {
              setCountryFilter(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><PublicIcon fontSize="small" sx={{ color: '#A4B0C3' }}/></InputAdornment>,
              sx: { 
                borderRadius: 3, 
                bgcolor: '#f8fafc',
                '& fieldset': { borderColor: '#e2e8f0', borderWidth: '2px !important' },
                '&:hover fieldset': { borderColor: '#cbd5e1' }
              }
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: { borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                }
              }
            }}
            sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.02)' } }}
          >
             {uniqueCountries.map(country => (
               <MenuItem key={country} value={country}>{country === 'All' ? 'All Countries' : country}</MenuItem>
             ))}
          </TextField>
          <TextField
            placeholder="Search leads..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#A4B0C3' }}/></InputAdornment>,
              sx: { 
                 borderRadius: 3, 
                 bgcolor: '#f8fafc',
                 '& fieldset': { borderColor: '#e2e8f0' },
                 '&:hover fieldset': { borderColor: '#cbd5e1' }
              }
            }}
            sx={{ flexGrow: 1, minWidth: { sm: 300 } }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }} 
            size="small"
            sx={{ 
                '& .MuiToggleButton-root': { 
                    color: '#94a3b8',
                    borderColor: '#e2e8f0',
                    '&.Mui-selected': { 
                        bgcolor: '#eff6ff', 
                        color: '#3b82f6',
                        borderColor: '#bfdbfe'
                    },
                    '&:hover': { bgcolor: '#f8fafc' }
                }
            }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        </Box>
      </Card>

      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {filteredLeads.map(lead => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={lead.leadid}>
              <Card elevation={0} sx={{ 
                  height: '100%', 
                  borderRadius: 4, 
                  border: '1px solid #f1f5f9',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)', 
                  cursor: 'pointer', 
                  transition: 'all 0.3s ease', 
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 28px rgba(0,0,0,0.08)', borderColor: '#cbd5e1' } 
                }} 
                onClick={() => navigate(`/leads/${lead.leadid}`)}
              >
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                      <Avatar sx={{ 
                          bgcolor: '#eff6ff', 
                          color: '#2563eb', 
                          width: 48, 
                          height: 48, 
                          fontWeight: '800',
                          fontSize: '1.25rem',
                          boxShadow: '0 4px 10px rgba(37,99,235,0.15)'
                      }}>
                        {lead.companyname ? lead.companyname.charAt(0).toUpperCase() : 'U'}
                      </Avatar>
                      <Box sx={{ overflow: 'hidden' }}>
                          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: '800', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {lead.companyname || 'Unnamed Lead'}
                          </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={getStatusLabel(lead.statecode)} 
                      color={getStatusColor(lead.statecode)} 
                      size="small" 
                      sx={{ fontWeight: 'bold', borderRadius: 2 }} 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 1, mt: 2 }}>
                    <BusinessIcon sx={{ fontSize: 18, mr: 1.5, color: '#94a3b8' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{lead.fullname || 'No Company'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 1 }}>
                    <EmailIcon sx={{ fontSize: 18, mr: 1.5, color: '#94a3b8' }} />
                    <Typography variant="body2" noWrap>{lead.emailaddress1 || 'N/A'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 1 }}>
                    <PhoneIcon sx={{ fontSize: 18, mr: 1.5, color: '#94a3b8' }} />
                    <Typography variant="body2" noWrap>{lead.telephone1 || 'N/A'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <PublicIcon sx={{ fontSize: 18, mr: 1.5, color: '#94a3b8' }} />
                    <Typography variant="body2" noWrap>{lead.address1_country || 'N/A'}</Typography>
                  </Box>
                </CardContent>
                <Box sx={{ px: 3, pb: 3, pt: 1, borderTop: '1px solid', borderColor: '#f1f5f9' }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                    Created: {new Date(lead.createdon).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
          {filteredLeads.length === 0 && (
             <Grid item xs={12}>
               <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
                 <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No leads found</Typography>
                 <Typography variant="body2" color="#94a3b8">Try adjusting your filters or search criteria.</Typography>
               </Paper>
             </Grid>
          )}
        </Grid>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', width: '100%', bgcolor: 'white' }}>
          <TableContainer>
            <Table stickyHeader sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2.5 } }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Name / Company</TableCell>
                  <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Country</TableCell>
                  <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Created On</TableCell>
                  <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedLeads.length > 0 ? (
                  displayedLeads.map((lead) => (
                    <TableRow hover key={lead.leadid} sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', transition: 'background-color 0.2s, transform 0.1s', '&:hover': { bgcolor: '#f8fbff !important' } }} onClick={() => navigate(`/leads/${lead.leadid}`)}>
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                          <Avatar sx={{ 
                              bgcolor: '#eff6ff', 
                              color: '#2563eb', 
                              fontWeight: '900', 
                              width: 46, height: 46, 
                              boxShadow: '0 4px 10px rgba(37,99,235,0.2)'
                          }}>
                            {lead.fullname ? lead.fullname.substring(0,2).toUpperCase() : 'U'}
                          </Avatar>
                          <Box>
                              <Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.95rem' }}>
                                {lead.fullname || 'Unnamed Lead'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: '800', color: '#3b82f6', bgcolor: '#eff6ff', px: 1, py: 0.25, borderRadius: 1.5 }}>
                                    {lead.companyname || 'N/A'}
                                  </Typography>
                              </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{lead.address1_country || 'N/A'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{lead.emailaddress1 || 'N/A'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{lead.telephone1 || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip label={getStatusLabel(lead.statecode)} color={getStatusColor(lead.statecode)} size="small" sx={{ fontWeight: 'bold', borderRadius: 2 }} />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{new Date(lead.createdon).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric'})}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton sx={{ color: '#1e3c72', '&:hover': { bgcolor: 'rgba(30,60,114,0.1)' } }} onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.leadid}`); }}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No matching leads found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredLeads.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      </Box>
      </Grow>
    </Box>
  );
};

export default Leads;